import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Apple } from "lucide-react";
import { usePayPalScriptReducer } from "@paypal/react-paypal-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Real one-tap Apple Pay via PayPal. Renders ONLY on eligible Apple devices
 * (Safari + a card in Wallet + domain registered in PayPal). On anything else
 * it renders nothing, so it can never interfere with the PayPal/Card buttons.
 *
 * IMPORTANT: it reuses the SINGLE PayPal SDK loaded by <PayPalScriptProvider>
 * (which must include components "buttons,applepay"). It must NOT inject its
 * own SDK <script> — two SDK scripts with the same client-id conflict and stop
 * the PayPal/Card buttons from rendering.
 */
export default function ApplePayButton({ pkg, onSuccess }) {
  const [{ isResolved }] = usePayPalScriptReducer();
  const [eligible, setEligible] = useState(false);
  const cfgRef = useRef(null);
  const applepayRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!isResolved) return;
    const AP = window.ApplePaySession;
    if (!(AP && AP.supportsVersion(4) && AP.canMakePayments())) return;
    const paypal = window.paypal;
    if (!paypal || typeof paypal.Applepay !== "function") return;
    (async () => {
      try {
        const applepay = paypal.Applepay();
        const c = await applepay.config();
        if (cancelled) return;
        if (c && c.isEligible) {
          applepayRef.current = applepay;
          cfgRef.current = c;
          setEligible(true);
        }
      } catch (e) { /* not eligible on this account/device */ }
    })();
    return () => { cancelled = true; };
  }, [isResolved]);

  if (!eligible || !pkg) return null;

  const pay = async () => {
    const applepay = applepayRef.current;
    const cfg = cfgRef.current;
    const paymentRequest = {
      countryCode: cfg.countryCode || "US",
      currencyCode: "USD",
      merchantCapabilities: cfg.merchantCapabilities,
      supportedNetworks: cfg.supportedNetworks,
      requiredBillingContactFields: ["postalAddress", "name"],
      total: { label: "BETohalics Credits", amount: Number(pkg.price_usd).toFixed(2) },
    };
    const session = new window.ApplePaySession(4, paymentRequest);
    session.onvalidatemerchant = async (event) => {
      try {
        const res = await applepay.validateMerchant({ validationUrl: event.validationURL });
        session.completeMerchantValidation(res.merchantSession);
      } catch (e) { session.abort(); }
    };
    session.onpaymentauthorized = async (event) => {
      try {
        const { data } = await axios.post(`${API}/payments/paypal/create-order`, {
          package_id: pkg.id, custom_amount: pkg.id === "custom" ? pkg.credits : undefined,
          save: false, funding: "applepay", origin: window.location.origin,
        });
        await applepay.confirmOrder({
          orderId: data.id,
          token: event.payment.token,
          billingContact: event.payment.billingContact,
        });
        const cap = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.id });
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        onSuccess(cap);
      } catch (e) {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      }
    };
    session.begin();
  };

  return (
    <button type="button" onClick={pay} data-testid="bc-applepay-button"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        width: "100%", height: 48, marginBottom: 12, borderRadius: 999, border: "none",
        background: "#000", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer",
      }}>
      <Apple size={18} fill="#fff" strokeWidth={0} /> Pay
    </button>
  );
}
